import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Progress,
  Typography,
  Badge,
  Flex,
  Space,
  Tooltip,
  Modal,
  Tabs
} from 'antd';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteDevice,
  apiGetDevicesInformation,
  apiGetDevicesInformationV2
} from '@services/ManageDevices/DeviceServices';
import { showError, showSuccess, showWarning } from '@components/notification';
import { communityList, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import {
  SearchOutlined,
  ClearOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
  EditFilled,
  DeleteFilled,
  ToolFilled,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { URL_API } from '@configs/index';
import TabPane from 'antd/es/tabs/TabPane';
import { useRole } from '@contexts/Auth/UseRole';
import { is } from 'date-fns/locale';

const { Option } = Select;
const { Title, Paragraph } = Typography;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const [custId, setCustId] = useState(null);
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const { isWebAdmin, isWebManager, isWebSuperAdmin } = useRole();

  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetDevicesInformationV2({
        page: currentPage,
        size: pageSize,
        keyWord: search ?? undefined,
        customerId: custId ?? undefined,
        status: status === 'all' ? undefined : status,
        type: type ?? undefined
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status, type]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleEdit = (record) => {
    navigate(`/device/${record.id}`);
  };

  const handleReplacement = (record) => {
    navigate(`/device/replace/${record.id}`);
  };
  const handleViewDetail = (record) => {
    navigate(`/device/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá thiết bị này?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteDevice(record?.id);
        showSuccess({ message: 'Xoá thiết bị thành công!' });
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        fixed: 'left',
        render: (text, record) => {
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record?.status === 'IN_USE' ? (
                  <CheckCircleFilled style={{ fontSize: '16px', color: '#52c41a' }} />
                ) : (
                  <CheckCircleFilled style={{ fontSize: '16px', color: '#5D5D5D' }} />
                )}
                <Typography.Text
                  style={{
                    fontSize: '12px',
                    color: record?.status === 'IN_USE' ? '#52c41a' : '#ff4d4f'
                  }}>
                  {record?.status === 'IN_USE' ? 'Đang sử dụng' : 'Lưu kho'}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 120
      },
      {
        title: 'Số serie',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Số IMEI',
        dataIndex: 'imei',
        width: 100
      },
      {
        title: 'Số IMSI',
        dataIndex: 'imsi',
        width: 100
      },
      {
        title: 'Model',
        dataIndex: 'model',
        width: 100
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {isWebSuperAdmin && (
              <>
                <Tooltip title="Chỉnh sửa">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
                {/* <Tooltip title="Thay thế thiết bị">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<ToolFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleReplacement(record)}
                  />
                </Tooltip> */}
                <Tooltip title="Xoá thiết bị">
                  <Button
                    type="danger"
                    shape="circle"
                    icon={<DeleteFilled style={{ color: '#0174CF', fontSize: '18px' }} />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        )
      }
    ],
    []
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleCustIdChange = (e) => {
    setCustId(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/devices/export-report`,
        'Danh sách thiết bị.xlsx',
        null,
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    navigate('/devices/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ color: '#0174CF' }}>
            Quản lý thiết bị
          </Typography.Title>
        </Col>
        <Col>
          {(isWebAdmin || isWebSuperAdmin) && (
            <Space>
              {isWebSuperAdmin && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={handleAddDevice}
                  style={{
                    color: '#0174CF',
                    height: '36px',
                    backgroundColor: '#E8EBEF',
                    borderColor: '#E8EBEF',
                    fontSize: '18px'
                  }}>
                  Thêm mới
                </Button>
              )}
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0174CF',
                  borderColor: '#0174CF',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
              {isWebSuperAdmin && (
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setVisibleModalImport(true)}
                  style={{
                    color: '#fff',
                    height: '36px',
                    backgroundColor: '#0174CF',
                    borderColor: '#0174CF',
                    fontSize: '18px'
                  }}>
                  Nhập file excel
                </Button>
              )}
            </Space>
          )}
        </Col>
      </Row>
      <Card bodyStyle={{ padding: '16px' }}>
        {/* <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col md={6} xs={12}>
            <Button
              onClick={() => setType(type === 'LD' ? '' : 'LD')}
              style={{
                backgroundColor: type === 'LD' ? '#0174CF' : '#E8EBEF',
                borderColor: type === 'LD' ? '#0174CF' : '#E8EBEF',
                color: type === 'LD' ? '#fff' : '#0174CF',
                height: '36px',
                width: '100%'
              }}>
              {type === 'LD' ? 'Thiết bị kiểm tra rò rỉ' : 'Thiết bị thông thường'}
            </Button>
          </Col>
        </Row> */}
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <SelectCommunity value={selectedCommunity} onChange={setSelectedCommunity} />
          </Col> */}
          <Col md={6} xs={12}>
            <Input
              value={search}
              onChange={handleSearchChange}
              placeholder="Tìm kiếm số serie, imei..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col>
          <Col span={6}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0174CF',
                borderColor: '#0174CF',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
        <Tabs activeKey={status} defaultActiveKey="all" onChange={(value) => setStatus(value)}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Đang sử dụng`} key={'IN_USE'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Lưu kho`} key={'STORED'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: '4px',
                thead: { backgroundColor: '#DBDFE5', color: '#626D7C' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
